import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useImperativeHandle, useContext, createContext, useEffect } from 'react';
import { LineIcon } from '../../ui/icons/LineIcon.js';
import { useDevice } from '../../../contexts/DeviceContext.js';
import { cn } from '../../../lib/utils.js';

const HiddenField = () => {
  return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center justify-center w-6 h-[48px] md:h-[56px] text-basic-05 heading-h2", children: /* @__PURE__ */ jsx(LineIcon, { icoName: "ico-pin", icoColor: "var(--color-bg-basic-background-01)" }) });
};
const SecurityFieldFull = forwardRef(
  ({
    type = "default",
    fieldIdx,
    length,
    value,
    onChange,
    error,
    disabled = false,
    readonly = false,
    masked = true,
    label: propLabel
  }, ref) => {
    const [focused, setFocused] = useState(false);
    const InputRef = useRef(null);
    const editableIndexes = type === "segmented" ? fieldIdx : Array.from({ length }).map((_, idx) => idx);
    useImperativeHandle(ref, () => ({
      focus: () => {
        InputRef?.current?.focus();
      },
      clear: () => {
        onChange("");
      }
    }));
    const getSlotIdx = (slotNum) => {
      return editableIndexes?.indexOf(slotNum) ?? -1;
    };
    const getSlotChar = (slotNum) => {
      const editableOrder = getSlotIdx(slotNum);
      return value[editableOrder] ?? null;
    };
    const { isDesktop } = useDevice();
    const isMouseInteractionRef = useRef(false);
    const { label: contextLabel } = useContext(SecurityLabelContext);
    const label = propLabel ?? contextLabel;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: "relative security-wrap group",
        onMouseDown: (e) => {
          isMouseInteractionRef.current = true;
          e.preventDefault();
        },
        onClick: () => {
          InputRef?.current?.focus();
        },
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: InputRef,
              type: "text",
              inputMode: "numeric",
              maxLength: fieldIdx ? fieldIdx.length : length,
              value,
              onChange: (e) => onChange(e.target.value),
              onFocus: () => setFocused(true),
              onBlur: () => {
                setFocused(false);
                isMouseInteractionRef.current = false;
              },
              className: cn(
                "caret-[transparent] text-[transparent] outline-none absolute inset-0 cursor-default selection:text-[transparent] not-readonly peer",
                !isMouseInteractionRef.current && isDesktop && "focus-visible:outline focus-visible:outline-offset-4 focus-visible:outline-black focusVisible",
                length === 4 && fieldIdx?.includes(0) && isDesktop && "w-[120px] left-0 right-auto",
                length === 4 && fieldIdx?.includes(3) && isDesktop && "w-[120px] right-0 left-auto"
              ),
              title: `${label ?? ""} ${type === "segmented" ? `${label} ${fieldIdx?.map((idx) => idx + 1).join(",")}번째 입력` : `${length}자리 입력`}`
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "flex gap-2 justify-center", "aria-hidden": "true", children: Array.from({ length }).map((_, idx) => {
            return type === "segmented" && !fieldIdx?.includes(idx) ? /* @__PURE__ */ jsx(HiddenField, {}, `hidden-field-${idx}`) : /* @__PURE__ */ jsx(
              "div",
              {
                className: "relative",
                title: `${getSlotChar(idx) ? `${label ? label + "" : ""} ${idx + 1}번째 입력됨` : `${label ? label + "" : ""} ${idx + 1}번째 입력}`}`,
                children: /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "w-[clamp(40px,12vw,48px)] h-[48px] rounded-[8px] p-0 border border-basic-04 flex items-center justify-center",
                      isDesktop && "w-[56px] h-[56px]",
                      error && "border-2  border-interactive-danger",
                      getSlotChar(idx) && "border-2 bg-primary-background-07 border-primary-03",
                      focused && value.length === getSlotIdx(idx) && "border-interactive-primary-default-01",
                      !isMouseInteractionRef.current && isDesktop && focused && value.length === getSlotIdx(idx) && " group-has-[input:focus-within]:border-basic-04",
                      disabled && "bg-state-disabled-01 border-state-disabled-02",
                      readonly && "bg-state-disabled-01 border-state-disabled-02"
                    ),
                    children: getSlotChar(idx) && (masked ? /* @__PURE__ */ jsx(
                      "span",
                      {
                        className: cn(
                          "inline-flex items-center justify-center w-6 h-6 text-primary-05 heading-h2",
                          readonly && "text-[var(--color-bg-basic-background-01)]"
                        ),
                        style: readonly ? {
                          color: "var(--color-bg-basic-background-01)"
                        } : void 0,
                        children: /* @__PURE__ */ jsx(
                          LineIcon,
                          {
                            icoName: "ico-pin",
                            icoColor: disabled || readonly ? "var(--color-bg-basic-background-01)" : "var(--color-text-primary-05)"
                          }
                        )
                      }
                    ) : /* @__PURE__ */ jsx(
                      "span",
                      {
                        className: cn(
                          "inline-flex items-center justify-center w-6 h-6 text-primary-05 heading-h2"
                        ),
                        children: getSlotChar(idx)
                      }
                    ))
                  }
                )
              },
              `edit-field-${idx}`
            );
          }) })
        ]
      }
    );
  }
);
const SecurityLabelContext = createContext({
  label: "",
  setLabel: () => {
  }
});
const SecurityArea = ({ children }) => {
  const [label, setLabel] = useState("계좌비밀번호");
  return /* @__PURE__ */ jsx(SecurityLabelContext.Provider, { value: { label, setLabel }, children: /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children }) });
};
const SecurityTitle = ({ children }) => {
  const { setLabel } = useContext(SecurityLabelContext);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    let text = "";
    for (const node of Array.from(ref.current.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
      else if (node.classList?.contains("text-primary-04")) break;
    }
    setLabel(text.trim());
  }, [setLabel]);
  return /* @__PURE__ */ jsx("div", { ref, className: "body-md font-weight-medium text-basic-04 text-center", children });
};
const SecurityContents = ({ children }) => {
  return /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center gap-2 flex-wrap", children });
};
const useSecurityFieldLabelContext = () => {
  const context = useContext(SecurityLabelContext);
  if (context === void 0) {
    throw new Error("useSecurityFieldLabelContext must be used within SecurityFieldArea");
  }
  return context;
};

export { HiddenField, SecurityArea, SecurityContents, SecurityFieldFull, SecurityTitle, useSecurityFieldLabelContext };
